import { useCallback, useContext, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast, cssTransition } from "react-toastify";
import { DynamicContext } from "../../Context/DynamicContext";
import { get } from "../../helper/Api_helper";
import {
  VYLABS_BASE_URL,
  WEB_SOCKET_URL,
  NOTIFICATIONS_GET,
} from "../../helper/url_helper";
import "./NotificationToast.css";
import liveNotiBgShapes from "../../Assets/Images/live_notification_BG_shape.svg";
import notiUserIcon from "../../Assets/Images/notification-user-icon.svg";

const PING_INTERVAL = 30000;
const PING_TIMEOUT = 10000;
const MAX_RECONNECT = 5;
const BASE_DELAY = 1000;

const RtSlide = cssTransition({
  enter: "rt-slide-in",
  exit: "rt-slide-out",
  collapse: false,
});

const parseData = (d) => {
  if (!d) return {};
  if (typeof d === "string") {
    try {
      return JSON.parse(d);
    } catch {
      return {};
    }
  }
  return d;
};

const getInitials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "VT";

function MessageToast({ title, body, assignerName, onOpen, closeToast }) {
  const handleRowClick = () => (onOpen ? onOpen() : closeToast());

  return (
    <div
      className="rt-inner"
      role="button"
      tabIndex={0}
      onClick={handleRowClick}
      onKeyDown={(e) => e.key === "Enter" && handleRowClick()}
      style={{
        backgroundImage: `url(${liveNotiBgShapes})`,
        backgroundSize: "auto 100%",
        backgroundPosition: "bottom left",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="rt-avatar">{getInitials(assignerName)}</div>
      <div className="rt-content">
        <div className="rt-meta">
          <span className="rt-app">VybogTAL</span>
        </div>
        <div className="rt-title">{title}</div>
        <div className="rt-body">
         { assignerName && ( <img src={notiUserIcon} alt="notiUserIcon"/> )} 
         {body}
        </div>
        {/* {onOpen && (
          <div className="rt-actions">
            <button
              className="rt-btn rt-btn-primary"
              onClick={(e) => {
                e.stopPropagation();
                onOpen();
              }}
            >
              View job
            </button>
            <button
              className="rt-btn rt-btn-ghost"
              onClick={(e) => {
                e.stopPropagation();
                closeToast();
              }}
            >
              Dismiss
            </button>
          </div>
        )} */}
      </div>
      <button
        className="rt-close"
        aria-label="Close"
        onClick={(e) => {
          e.stopPropagation();
          closeToast();
        }}
      >
        ×
      </button>
    </div>
  );
}

export default function NotificationSocketManager() {
  const { userDetails, setNotificationsState } = useContext(DynamicContext);
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const seenIdsRef = useRef(new Set());
  const wsRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const pingTimeoutRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const attemptsRef = useRef(0);
  const unmountedRef = useRef(false);

  const showToast = useCallback((n) => {
    const data = parseData(n.data);
    const isJob = n.type === "job_assigned";
    const openJob =
      isJob && data.job_id
        ? () =>
            navigateRef.current("/jobs-list", {
              state: { openJobId: data.job_id },
            })
        : undefined;
    toast(
      ({ closeToast }) => (
        <MessageToast
          title={n.title}
          body={n.body || n.message}
          assignerName={data.assigned_by_name}
          closeToast={closeToast}
          onOpen={
            openJob
              ? () => {
                  closeToast();
                  openJob();
                }
              : undefined
          }
        />
      ),
      {
        toastId: n.id,
        position: "bottom-right",
        autoClose: 8000,
        closeButton: false,
        icon: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        transition: RtSlide,
        className: "rt-toast",
        bodyClassName: "rt-toast-body",
        progressClassName: "rt-progress",
      }
    );
    if (
      document.hidden &&
      "Notification" in window &&
      Notification.permission === "granted"
    ) {
      try {
        const native = new Notification(n.title || "VybogTAL", {
          body: n.body || n.message,
          tag: String(n.id),
          icon: "/favicon.ico",
        });
        native.onclick = () => {
          window.focus();
          if (openJob) openJob();
          native.close();
        };
      } catch (e) {
        console.error("Native notification error", e);
      }
    }
  }, []);

  const handlePayload = useCallback(
    (n) => {
      if (!n?.id) return;
      const isNew = !seenIdsRef.current.has(n.id);
      seenIdsRef.current.add(n.id);

      setNotificationsState((prev) => {
        const list = Array.isArray(prev?.notifications)
          ? prev.notifications
          : [];
        const exists = list.some((i) => i.id === n.id);
        return {
          ...prev,
          notifications: exists
            ? list.map((i) => (i.id === n.id ? { ...i, ...n } : i))
            : [n, ...list],
          unread_count:
            typeof n.unread_count === "number"
              ? n.unread_count
              : (prev?.unread_count || 0) + (exists ? 0 : 1),
        };
      });

      if (isNew) showToast(n);
    },
    [setNotificationsState, showToast]
  );

  const handlePayloadRef = useRef(handlePayload);
  handlePayloadRef.current = handlePayload;

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token || !userDetails?.id) return undefined;
    unmountedRef.current = false;

    // Initial badge fetch (no toasts for already-existing notifications)
    get(VYLABS_BASE_URL, `${NOTIFICATIONS_GET}?unread_only=true`)
      .then((res) => {
        (res?.notifications || []).forEach((n) => seenIdsRef.current.add(n.id));
        setNotificationsState({
          notifications: res?.notifications || [],
          unread_count: res?.unread_count || 0,
        });
      })
      .catch((e) => console.error("notifications fetch error", e));

    const clearTimers = () => {
      clearInterval(pingIntervalRef.current);
      clearTimeout(pingTimeoutRef.current);
      clearTimeout(reconnectTimeoutRef.current);
    };

    const connect = () => {
      const host = String(WEB_SOCKET_URL)
        .replace(/^(wss?|https?):\/\//, "")
        .replace(/^\/+/, "");
      const url = `wss://${host}/ws?token=${encodeURIComponent(
        "Bearer " + localStorage.getItem("token")
      )}&channel_id=${encodeURIComponent(userDetails.id)}`;
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        attemptsRef.current = 0;
        const ping = () => {
          if (ws.readyState !== WebSocket.OPEN) return;
          ws.send("ping");
          clearTimeout(pingTimeoutRef.current);
          pingTimeoutRef.current = setTimeout(
            () => ws.close(4000, "ping timeout"),
            PING_TIMEOUT
          );
        };
        ping();
        pingIntervalRef.current = setInterval(ping, PING_INTERVAL);
      };

      ws.onmessage = (event) => {
        if (event.data === "pong") {
          clearTimeout(pingTimeoutRef.current);
          return;
        }
        try {
          const [topic, payload] = event.data.trim().split(/\|(.+)/, 2);
          if (topic?.split(":")[0] !== "notification" || !payload) return;
          handlePayloadRef.current(JSON.parse(payload));
        } catch (e) {
          console.error("WS message error", e);
        }
      };

      ws.onclose = (event) => {
        clearInterval(pingIntervalRef.current);
        clearTimeout(pingTimeoutRef.current);
        if (
          !unmountedRef.current &&
          event.code !== 1000 &&
          attemptsRef.current < MAX_RECONNECT
        ) {
          const delay = Math.min(BASE_DELAY * 2 ** attemptsRef.current, 30000);
          reconnectTimeoutRef.current = setTimeout(() => {
            attemptsRef.current += 1;
            connect();
          }, delay);
        }
      };
    };

    const t = setTimeout(connect, 250);

    return () => {
      unmountedRef.current = true;
      clearTimeout(t);
      clearTimers();
      wsRef.current?.close(1000, "cleanup");
      wsRef.current = null;
    };
  }, [userDetails?.id, setNotificationsState]);

  return null;
}